import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface ContactData {
  id: string;
  country: string;
  flagUrl: string;
  phone: string;
  address: string;
}

const contacts: ContactData[] = [
  {
    id: 'usa',
    country: 'United States of America',
    flagUrl: 'https://flagcdn.com/w160/us.png',
    phone: '+1 469 393 8422',
    address: 'New York, NY',
  },
  {
    id: 'uk',
    country: 'United Kingdom',
    flagUrl: 'https://flagcdn.com/w160/gb.png',
    phone: '+44 784 686 7434',
    address: 'London, England',
  },
  // {
  //   id: 'uae',
  //   country: 'UAE',
  //   flagUrl: 'https://flagcdn.com/w160/ae.png',
  //   phone: '+971 52 123 4567',
  //   address: 'Dubai, UAE',
  // },
  {
    id: 'india',
    country: 'India',
    flagUrl: 'https://flagcdn.com/w160/in.png',
    phone: '+91 995 953 5367',
    address: 'Hyderabad, Telangana',
  },
  {
    id: 'singapore',
    country: 'Singapore',
    flagUrl: 'https://flagcdn.com/w160/sg.png',
    phone: '+65 8612 1164',
    address: 'Singapore City',
  },
  {
    id: 'australia',
    country: 'Australia',
    flagUrl: 'https://flagcdn.com/w160/au.png',
    phone: '+1 770 869 2981',
    address: 'Sydney, NSW',
  },
];

export default function GlobalPresence() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [activeId, setActiveId] = useState<string | null>(null);

  return (
    <section
      id="locations"
      ref={ref}
      className="py-16 md:py-24 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-global-line text-primary-600"></i>
            <span className="text-sm font-medium text-gray-700">
              Global Presence
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Delivering Excellence
            <br />
            <span className="text-primary-600">Across the Globe</span>
          </motion.h2>
        </motion.div>

        {/* Contact Cards Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7, delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
        >
          {contacts.map((contact, index) => {
            const isActive = activeId === contact.id;
            return (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ ...springTransition, duration: 0.6, delay: 0.25 + index * 0.08 }}
                onMouseEnter={() => setActiveId(contact.id)}
                onMouseLeave={() => setActiveId(null)}
                className={`relative group rounded-2xl border-2 p-5 md:p-6 transition-all duration-300 cursor-default ${
                  isActive
                    ? 'bg-primary-50 border-primary-300 shadow-lg -translate-y-1'
                    : 'bg-white border-gray-100 hover:border-primary-200 hover:shadow-md hover:-translate-y-1'
                }`}
              >
                {/* Flag */}
                <div className="flex items-start justify-between mb-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
                    className={`w-14 h-10 rounded-md overflow-hidden shadow-sm border border-gray-200 transition-transform duration-300 ${
                      isActive ? 'scale-110' : ''
                    }`}
                  >
                    <img
                      src={contact.flagUrl}
                      alt={`${contact.country} flag`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.35 + index * 0.08, ...springTransition }}
                    className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 ${
                      isActive
                        ? 'bg-primary-100 text-primary-600 scale-110'
                        : 'bg-gray-100 text-gray-400 group-hover:bg-primary-50 group-hover:text-primary-500'
                    }`}
                  >
                    <i className="ri-phone-line text-lg"></i>
                  </motion.div>
                </div>

                {/* Info */}
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.08, duration: 0.4 }}
                  className="font-bold text-navy-900 text-lg mb-1"
                >
                  {contact.country}
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.42 + index * 0.08, duration: 0.4 }}
                  className="text-gray-500 text-sm mb-3 flex items-center gap-1.5"
                >
                  <i className="ri-map-pin-line text-gray-400"></i>
                  {contact.address}
                </motion.p>

                {/* Phone */}
                <motion.a
                  initial={{ opacity: 0, y: 10 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.45 + index * 0.08, duration: 0.4 }}
                  href={`tel:${contact.phone.replace(/\s/g, '')}`}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    isActive
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-gray-50 text-primary-600 hover:bg-primary-50'
                  }`}
                >
                  <i className="ri-phone-fill"></i>
                  {contact.phone}
                </motion.a>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}